import { useCallback } from "react";
import { useCreateSubscriptionMutation } from "src/api/services/patientSubscriptionApi";
import { useSetDefaultPaymentMethodMutation } from "src/api/services/paymentMethodsApi";
import { ProductPrice } from "src/api/types";
import { useTrack } from "./useTrack";

type OnSuccessParams = {
  paymentMethodId?: string;
  callbacks?: (() => void)[];
};

type SuccessPaymentParams = {
  price: ProductPrice;
  couponId?: number;
};

export const useSuccessPayment = ({
  price,
  couponId,
}: SuccessPaymentParams) => {
  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const [createSubscription] = useCreateSubscriptionMutation();
  const track = useTrack();

  return useCallback(
    ({ paymentMethodId, callbacks }: OnSuccessParams) => {
      const promises: Promise<unknown>[] = [];

      if (price.product.billing_model === "subscription") {
        promises.push(
          createSubscription({
            status: "draft",
            price: price.stripe_id,
            initial: true,
            coupon_id: couponId,
          }).unwrap()
        );
      }

      if (paymentMethodId != null) {
        promises.push(
          setDefaultPaymentMethod({
            paymentMethodId,
          }).unwrap()
        );
      }

      return Promise.allSettled(promises).then(() => {
        track("completed-payment-page", {
          product: price.product.slug,
          price: price.amount,
        });
        callbacks?.forEach((callback) => callback());
      });
    },
    [
      couponId,
      createSubscription,
      price.amount,
      price.product.billing_model,
      price.product.slug,
      price.stripe_id,
      setDefaultPaymentMethod,
      track,
    ]
  );
};
